import UIButton, { UIButtonProps } from 'UIComponents/button/UIButton';
import styled, { css } from 'foundations-theming-specialty/utils';
import { CSSObject } from 'foundations-theming/utils';
import { BREEZE_SPECIAL } from '../../../../constants/BreezeUse';
import { AIBaseButtonProps } from '../AIBaseButton';

const DropdownButtonIconStyle = css`
  .uiDropdown__buttonContents {
    display: inline-block;
    .uiDropdown__buttonLabel {
      margin-right: ${({ theme }) => theme.space['200']};
    }
  }
  [class^='AbstractDropdown'] {
    display: inline;
    [class^='DropdownCaret'] {
      margin-left: ${({ theme }) => theme.space['200']};
    }
  }
`;

const IconStyle = css`
  .ai-icon {
    position: relative;
  }
`;

type StyledUIButtonProps = UIButtonProps & {
  cssstyles?: CSSObject;
};

export const BreezePrimaryAIBaseButton = styled(UIButton).withConfig({
  shouldForwardProp: (prop: string | number) => prop !== 'cssstyles',
})<StyledUIButtonProps>`
  border-radius: ${({ theme }) => theme.borderRadius['full']} !important;
  color: ${({ theme }) => theme.color['fill-surface-default']} !important;
  border: none !important;
  background: ${({ theme }) =>
    theme.color['breeze-fill-primary-default']} !important;
  &:hover {
    background-color: ${({ theme }) =>
      theme.color['breeze-fill-primary-hover']} !important;
  }
  &:active {
    background-color: ${({ theme }) =>
      theme.color['breeze-fill-primary-pressed']} !important;
  }
  ${DropdownButtonIconStyle}
  ${IconStyle}

  &&& {
    ${({ cssstyles }) => cssstyles}
    &.disabled {
      color: ${({ theme }) => theme.color['text-core-disabled']} !important;
      border-color: ${({ theme }) =>
        theme.color['border-core-subtle-pressed']} !important;
    }
  }
`;

export const BreezeSecondaryAIBaseButton = styled(UIButton).withConfig({
  shouldForwardProp: (prop: string | number) => prop !== 'cssstyles',
})<StyledUIButtonProps>`
  border-radius: ${({ theme }) => theme.borderRadius['full']} !important;
  color: ${({ theme }) =>
    theme.color['breeze-fill-primary-default']} !important;
  border-color: ${({ theme }) =>
    theme.color['breeze-border-field-default']} !important;
  background: ${({ theme }) => theme.color['fill-surface-default']} !important;
  &:hover {
    color: ${({ theme }) =>
      theme.color['breeze-fill-primary-hover']} !important;
    border-color: ${({ theme }) =>
      theme.color['breeze-fill-primary-hover']} !important;
    background: ${({ theme }) =>
      theme.color['breeze-fill-secondary-hover']} !important;
  }
  &:active {
    color: ${({ theme }) =>
      theme.color['breeze-fill-primary-pressed']} !important;
    border-color: ${({ theme }) =>
      theme.color['breeze-fill-primary-pressed']} !important;
    background: ${({ theme }) =>
      theme.color['breeze-fill-secondary-pressed']} !important;
  }
  ${DropdownButtonIconStyle}
  ${IconStyle}

  &&& {
    ${({ cssstyles }) => cssstyles}
    &.disabled {
      color: ${({ theme }) => theme.color['text-core-disabled']} !important;
      border-color: ${({ theme }) =>
        theme.color['border-core-subtle-pressed']} !important;
    }
  }
`;

export const BreezeTertiaryAIBaseButton = styled(UIButton).withConfig({
  shouldForwardProp: (prop: string | number) => prop !== 'cssstyles',
})<StyledUIButtonProps>`
  border-radius: ${({ theme }) => theme.borderRadius['full']} !important;
  color: ${({ theme }) => theme.color['text-core-default']} !important;
  border-color: ${({ theme }) => theme.color['text-core-default']} !important;
  background: ${({ theme }) => theme.color['fill-surface-default']} !important;
  &:hover {
    color: ${({ theme }) =>
      theme.color['fill-accent-neutral-hover']} !important;
    border-color: ${({ theme }) =>
      theme.color['fill-accent-neutral-hover']} !important;
  }
  &:active {
    color: ${({ theme }) =>
      theme.color['fill-accent-neutral-pressed']} !important;
    border-color: ${({ theme }) =>
      theme.color['fill-accent-neutral-pressed']} !important;
  }

  ${DropdownButtonIconStyle}
  ${IconStyle}

  &&& {
    ${({ cssstyles }) => cssstyles}

    &.disabled {
      color: ${({ theme }) => theme.color['text-core-disabled']} !important;
      border-color: ${({ theme }) =>
        theme.color['border-core-subtle-pressed']} !important;
    }
  }
`;

export const BreezeSpecialAIBaseButton = styled(UIButton).withConfig({
  shouldForwardProp: (prop: string | number) => prop !== 'cssstyles',
})<StyledUIButtonProps>`
  border-radius: ${({ theme }) => theme.borderRadius['full']} !important;
  &:not(.disabled) {
    background: ${({ theme }) =>
      theme.color['breeze-fill-special-btn-alt-default']} !important;
    color: ${({ theme }) => theme.color['fill-surface-default']} !important;
    border: none !important;
    &:hover {
      background: ${({ theme }) => theme.color['breeze-fill-primary-hover']};
    }
    &:active {
      background: ${({ theme }) => theme.color['breeze-fill-primary-pressed']};
    }
    &.disabled {
      background: none;
    }
  }
  ${DropdownButtonIconStyle}
  ${IconStyle}

  &&& {
    ${({ cssstyles }) => cssstyles}
    &.disabled {
      color: ${({ theme }) => theme.color['text-core-disabled']} !important;
      border-color: ${({ theme }) =>
        theme.color['border-core-subtle-pressed']} !important;
    }
  }
`;

export const BreezeTransparentAIBaseButton = styled(UIButton).withConfig({
  shouldForwardProp: (prop: string | number) => prop !== 'cssstyles',
})<StyledUIButtonProps>`
  border-radius: ${({ theme }) => theme.borderRadius['full']} !important;
  border: none !important;
  color: ${({ theme }) =>
    theme.color['breeze-fill-primary-default']} !important;
  &:not(.disabled) .ai-icon,
  &:not(.disabled) span {
    color: ${({ theme }) =>
      theme.color['breeze-fill-primary-default']} !important;
  }
  &:hover {
    color: ${({ theme }) =>
      theme.color['breeze-fill-primary-hover']} !important;
  }
  &:active {
    color: ${({ theme }) =>
      theme.color['breeze-fill-primary-pressed']} !important;
  }
  ${DropdownButtonIconStyle}
  ${IconStyle}

  &&& {
    ${({ cssstyles }) => cssstyles}
    &.disabled {
      color: ${({ theme }) => theme.color['text-core-disabled']} !important;
      border-color: ${({ theme }) =>
        theme.color['border-core-subtle-pressed']} !important;
    }
  }
`;

export const getStyledAIButton = (use: AIBaseButtonProps['use']) => {
  switch (use) {
    case BREEZE_SPECIAL:
      return BreezeSpecialAIBaseButton;
    case 'primary':
      return BreezePrimaryAIBaseButton;
    case 'secondary':
      return BreezeSecondaryAIBaseButton;
    case 'tertiary':
      return BreezeTertiaryAIBaseButton;
    case 'transparent':
      return BreezeTransparentAIBaseButton;
    default:
      return BreezePrimaryAIBaseButton;
  }
};
